import { Component, model, input, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ProjectService } from '../../../../services/project.service';
import { BlockScheduleModel } from '../../../../models/blockschedule.model';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-skids-control',
  standalone: true,
  imports: [CommonModule, FormsModule, Select],
  template: `
    <div class="checklist-control-row">
      <label [for]="'skids-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input">
        <p-select
          [inputId]="'skids-' + lineItem().lineItemId"
          [(ngModel)]="selectedSkidId"
          (onChange)="onSkidChange($event.value)"
          [options]="skidOptions()"
          optionLabel="displayName"
          optionValue="blockScheduleId"
          [disabled]="disabled() || loading()"
          [invalid]="hasError()"
          [placeholder]="loading() ? 'Loading MV Power Stations...' : 'Select MV Power Station...'"
          [filter]="true"
          filterBy="displayName"
          [appendTo]="'body'"
          styleClass="w-full"
        />
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class SkidsControlComponent implements OnInit {
  lineItem = input.required<LineItemModel>();
  value = model<number | null>(null);
  disabled = input<boolean>(false);
  projectId = input<number>(0);
  showError = input<boolean>(false);

  loading = signal<boolean>(false);
  skidOptions = signal<{ blockScheduleId: number; displayName: string; tmeicSkidNo: number }[]>([]);
  selectedSkidId: number | null = null;
  touched = signal(false);

  private speechService = inject(SpeechService);
  private projectService = inject(ProjectService);
  private validationService = inject(ValidationService);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.selectedSkidId);
  });

  hasError = computed(() => this.errors().length > 0);

  ngOnInit(): void {
    this.selectedSkidId = this.normalizeToNumber(this.value());
    this.loadSkids();
  }

  ngOnChanges(): void {
    this.selectedSkidId = this.normalizeToNumber(this.value());
  }

  onSkidChange(value: any): void {
    const parsed = this.normalizeToNumber(value);
    this.selectedSkidId = parsed;
    this.value.set(parsed);
    this.touched.set(true);
  }

  private normalizeToNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && (parsed === -1 || parsed > 0) ? parsed : null;
  }

  private loadSkids(): void {
    const projectId = this.projectId();
    if (!projectId || projectId <= 0) {
      this.skidOptions.set([]);
      return;
    }

    this.loading.set(true);
    this.projectService.getProjectBlockSchedule(projectId).subscribe({
      next: (skids: BlockScheduleModel[]) => {
        const options = (skids || [])
          .filter(s => Number.isFinite(Number(s.blockScheduleId)))
          .map(s => {
            const base = (s.name || s.blockName || `MV Power Station #${s.tmeicSkidNo ?? ''}`).toString().trim();
            const config = this.parseSkidConfigLabel(s.skidConfiguration);
            return {
              blockScheduleId: Number(s.blockScheduleId),
              displayName: config ? `${base} | ${config}` : base,
              tmeicSkidNo: Number.isFinite(Number(s.tmeicSkidNo)) ? Number(s.tmeicSkidNo) : Number.MAX_SAFE_INTEGER
            };
          })
          .sort((a, b) => {
            if (a.tmeicSkidNo !== b.tmeicSkidNo) {
              return a.tmeicSkidNo - b.tmeicSkidNo;
            }
            return a.blockScheduleId - b.blockScheduleId;
          });

        this.skidOptions.set([
          { blockScheduleId: -1, displayName: 'N/A', tmeicSkidNo: -1 },
          ...options
        ]);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading skids for skids control:', error);
        this.loading.set(false);
        this.skidOptions.set([]);
      }
    });
  }

  private parseSkidConfigLabel(skidConfiguration?: string | null): string {
    if (!skidConfiguration) return '';
    const raw = skidConfiguration.trim();
    // Already friendly format e.g. "N4"
    if (!/^\d/.test(raw)) return raw;
    // Raw format e.g. "0-0-0-1-2-0-0"
    const nTypes = raw.split('-')
      .map((val, i) => ({ n: i + 1, count: parseInt(val, 10) }))
      .filter(x => x.count > 0)
      .map(x => `N${x.n}`);
    return nTypes.join(' | ');
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
